import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, MessageSquare, Phone, Search, X } from "lucide-react";
import { useAdminPeerChatInbox } from "../hooks/useAdminPeerChat";
import { useAdminPresenceHeartbeat } from "../hooks/useAdminPresenceHeartbeat";
import { useSellerAdminChatInbox } from "../hooks/useSellerAdminChat";
import { useSellerAdminVoice } from "../hooks/useSellerAdminVoice";
import { SellerAdminChatContact } from "../types";
import { AdminChatWindow } from "./AdminChatWindow";
import { ActiveVoiceCallPanel } from "./ActiveVoiceCallPanel";
import {
  Chevron,
  ContactAvatar,
  ContactAvatarWrap,
  ContactBadge,
  ContactButton,
  Dock,
  DockBar,
  DockBottom,
  DockLabel,
  DockPanel,
  DockWindows,
  DockWindow,
  IncomingBanner,
  IncomingBannerActions,
  IncomingBannerAvatar,
  IncomingBannerMeta,
  OnlineDot,
  PanelClose,
  PanelEmpty,
  PanelHead,
  PanelList,
  PanelSearch,
  PanelTab,
  PanelTabs,
  PanelTitleWrap,
  TotalBadge,
  WindowActions,
  WindowBtn,
  WindowHeader,
  WindowTitle,
} from "./dockStyles";
import {
  filterContacts,
  formatRelativeTime,
  getContactKey,
  getInitials,
  sortContacts,
} from "./utils";
import { getVoiceInitials } from "../utils/voiceCall";

const MAX_WINDOWS = 3;

type DockTab = "sellers" | "team";

type OpenWindow = {
  contact: SellerAdminChatContact;
  minimized: boolean;
};

export function SellerAdminChatDock() {
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DockTab>("sellers");
  const [search, setSearch] = useState("");
  const [openWindows, setOpenWindows] = useState<OpenWindow[]>([]);
  const pendingContactRef = useRef<{ scope: DockTab; id: number } | null>(null);

  useAdminPresenceHeartbeat();

  const sellerInboxQuery = useSellerAdminChatInbox();
  const teamInboxQuery = useAdminPeerChatInbox();

  const sellerContacts = useMemo(
    () =>
      (sellerInboxQuery.data?.data.contacts ?? []).map((contact) => ({
        ...contact,
        scope: contact.scope ?? "seller",
      })),
    [sellerInboxQuery.data?.data.contacts],
  );

  const teamContacts = useMemo(
    () =>
      (teamInboxQuery.data?.data.contacts ?? []).map((contact) => ({
        ...contact,
        scope: contact.scope ?? "admin_peer",
      })),
    [teamInboxQuery.data?.data.contacts],
  );

  const activeContacts = activeTab === "team" ? teamContacts : sellerContacts;
  const activeInboxQuery = activeTab === "team" ? teamInboxQuery : sellerInboxQuery;
  const voiceContacts = useMemo(
    () => [...sellerContacts, ...teamContacts],
    [sellerContacts, teamContacts],
  );
  const voiceStatus =
    sellerInboxQuery.data?.data.voice ?? teamInboxQuery.data?.data.voice;
  const voice = useSellerAdminVoice(voiceStatus, voiceContacts);

  const totalUnread =
    (sellerInboxQuery.data?.data.total_unread ?? 0) +
    (teamInboxQuery.data?.data.total_unread ?? 0);

  const sortedContacts = useMemo(
    () => filterContacts(sortContacts(activeContacts), search),
    [activeContacts, search],
  );

  const refetchInboxes = useCallback(() => {
    sellerInboxQuery.refetch();
    teamInboxQuery.refetch();
  }, [sellerInboxQuery, teamInboxQuery]);

  const openChat = useCallback((contact: SellerAdminChatContact) => {
    if (contact.scope === "admin_peer") {
      setActiveTab("team");
    } else {
      setActiveTab("sellers");
    }

    setPanelOpen(true);
    setOpenWindows((current) => {
      const contactKey = getContactKey(contact);
      const existing = current.find(
        (item) => getContactKey(item.contact) === contactKey,
      );
      if (existing) {
        return current.map((item) =>
          getContactKey(item.contact) === contactKey
            ? { ...item, minimized: false }
            : item,
        );
      }

      const next = [...current, { contact, minimized: false }];
      if (next.length > MAX_WINDOWS) next.shift();
      return next;
    });
  }, []);

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ sellerId?: number }>).detail;
      setPanelOpen(true);
      setActiveTab("sellers");

      if (detail?.sellerId) {
        const contact = sellerContacts.find((item) => item.id === detail.sellerId);
        if (contact) {
          openChat(contact);
        } else {
          pendingContactRef.current = { scope: "sellers", id: detail.sellerId };
        }
      }
    };

    window.addEventListener("admin-chat:open", handleOpen);
    return () => window.removeEventListener("admin-chat:open", handleOpen);
  }, [openChat, sellerContacts]);

  useEffect(() => {
    if (!pendingContactRef.current) return;

    const pending = pendingContactRef.current;
    const contacts = pending.scope === "team" ? teamContacts : sellerContacts;
    const contact = contacts.find((item) => item.id === pending.id);

    if (contact) {
      openChat(contact);
      pendingContactRef.current = null;
    }
  }, [openChat, sellerContacts, teamContacts]);

  const handleVoiceAction = useCallback(
    async (contact: SellerAdminChatContact) => {
      if (!voice.enabled || !contact.call_identity) return;
      if (voice.status !== "idle") return;

      try {
        await voice.call({
          identity: contact.call_identity,
          name: contact.name,
          photoUrl: contact.photo_url,
        });
      } catch {
        window.alert("Não foi possível iniciar a ligação.");
      }
    },
    [voice],
  );

  const panelSubtitle =
    activeTab === "team"
      ? "Conversas com administradores"
      : "Conversas com vendedores";

  const emptyTitle =
    activeTab === "team"
      ? "Nenhum administrador encontrado"
      : "Nenhum vendedor encontrado";

  const searchPlaceholder =
    activeTab === "team" ? "Buscar administrador..." : "Buscar vendedor...";

  return (
    <Dock $panelOpen={panelOpen} className="sc-dock sc-dock--seller">
      {voice.activeCallDisplay &&
      (voice.status === "connecting" || voice.status === "in-call") ? (
        <ActiveVoiceCallPanel
          contact={voice.activeCallDisplay}
          status={voice.status}
          callStartedAt={voice.callStartedAt}
          muted={voice.muted}
          onToggleMute={voice.toggleMute}
          onHangUp={voice.hangUp}
        />
      ) : null}
      {voice.incomingCall && voice.status === "idle" ? (
        <IncomingBanner>
          <IncomingBannerAvatar $photo={Boolean(voice.incomingCall.photoUrl)}>
            {voice.incomingCall.photoUrl ? (
              <img src={voice.incomingCall.photoUrl} alt={voice.incomingCall.name} />
            ) : (
              getVoiceInitials(voice.incomingCall.name)
            )}
          </IncomingBannerAvatar>
          <IncomingBannerMeta>
            <strong>{voice.incomingCall.name}</strong>
            <span>Ligação de voz</span>
          </IncomingBannerMeta>
          <IncomingBannerActions>
            <button type="button" onClick={voice.acceptIncoming}>
              Atender
            </button>
            <button type="button" onClick={voice.rejectIncoming}>
              Recusar
            </button>
          </IncomingBannerActions>
        </IncomingBanner>
      ) : null}
      <DockBottom>
        <DockWindows>
          {openWindows.map(({ contact, minimized }) => {
            const contactKey = getContactKey(contact);

            return (
              <DockWindow key={contactKey} $minimized={minimized}>
                <WindowHeader
                  onClick={() =>
                    setOpenWindows((current) =>
                      current.map((item) =>
                        getContactKey(item.contact) === contactKey
                          ? { ...item, minimized: !item.minimized }
                          : item,
                      ),
                    )
                  }
                >
                  <WindowTitle>
                    <strong>{contact.name}</strong>
                    {contact.subtitle ? <span>{contact.subtitle}</span> : null}
                    {contact.is_online ? (
                      <span className="text-[10px] text-emerald-200">Online</span>
                    ) : null}
                  </WindowTitle>
                  <WindowActions>
                  {voice.enabled &&
                  contact.call_identity &&
                  voice.status === "idle" ? (
                    <WindowBtn
                      type="button"
                      aria-label="Ligar"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleVoiceAction(contact);
                      }}
                    >
                      <Phone size={14} />
                    </WindowBtn>
                  ) : null}
                    <WindowBtn
                      type="button"
                      aria-label="Minimizar"
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenWindows((current) =>
                          current.map((item) =>
                            getContactKey(item.contact) === contactKey
                              ? { ...item, minimized: !item.minimized }
                              : item,
                          ),
                        );
                      }}
                    >
                      —
                    </WindowBtn>
                    <WindowBtn
                      type="button"
                      aria-label="Fechar"
                      onClick={(event) => {
                        event.stopPropagation();
                        setOpenWindows((current) =>
                          current.filter(
                            (item) => getContactKey(item.contact) !== contactKey,
                          ),
                        );
                      }}
                    >
                      ×
                    </WindowBtn>
                  </WindowActions>
                </WindowHeader>
                {!minimized && (
                  <AdminChatWindow
                    contact={contact}
                    onRead={refetchInboxes}
                  />
                )}
              </DockWindow>
            );
          })}
        </DockWindows>

        {panelOpen && (
          <DockPanel>
            <PanelHead>
              <PanelTitleWrap>
                <h3>Mensagens</h3>
                <span>{panelSubtitle}</span>
              </PanelTitleWrap>
              <PanelClose
                type="button"
                aria-label="Fechar lista"
                onClick={() => setPanelOpen(false)}
              >
                <X size={18} />
              </PanelClose>
            </PanelHead>

            <PanelTabs>
              <PanelTab
                type="button"
                $active={activeTab === "sellers"}
                onClick={() => {
                  setActiveTab("sellers");
                  setSearch("");
                }}
              >
                Vendedores
              </PanelTab>
              <PanelTab
                type="button"
                $active={activeTab === "team"}
                onClick={() => {
                  setActiveTab("team");
                  setSearch("");
                }}
              >
                Equipe interna
              </PanelTab>
            </PanelTabs>

            <PanelSearch>
              <Search size={16} />
              <input
                type="search"
                value={search}
                placeholder={searchPlaceholder}
                onChange={(event) => setSearch(event.target.value)}
              />
            </PanelSearch>

            <PanelList>
              {activeInboxQuery.isLoading && (
                <PanelEmpty>
                  <p>Carregando...</p>
                </PanelEmpty>
              )}

              {!activeInboxQuery.isLoading && !sortedContacts.length && (
                <PanelEmpty>
                  <p>{emptyTitle}</p>
                  <span>Tente buscar por nome ou e-mail.</span>
                </PanelEmpty>
              )}

              {sortedContacts.map((contact) => {
                const contactKey = getContactKey(contact);
                const isOpen = openWindows.some(
                  (item) => getContactKey(item.contact) === contactKey,
                );

                return (
                  <ContactButton
                    key={contactKey}
                    type="button"
                    $active={isOpen}
                    $unread={contact.unread_count > 0}
                    onClick={() => openChat(contact)}
                  >
                    <ContactAvatarWrap>
                      <ContactAvatar $photo={Boolean(contact.photo_url)}>
                        {contact.photo_url ? (
                          <img
                            src={contact.photo_url}
                            alt={contact.name}
                            loading="lazy"
                          />
                        ) : (
                          getInitials(contact.name)
                        )}
                      </ContactAvatar>
                      {contact.is_online ? (
                        <OnlineDot aria-label="Online" title="Online" />
                      ) : null}
                    </ContactAvatarWrap>
                    <span className="min-w-0">
                      <strong className="block text-sm text-slate-900 truncate">
                        {contact.name}
                        {contact.is_online ? (
                          <span className="ml-1 text-[10px] font-medium text-emerald-600">
                            online
                          </span>
                        ) : null}
                      </strong>
                      {contact.subtitle ? (
                        <span className="block text-[11px] text-slate-500 truncate">
                          {contact.subtitle}
                        </span>
                      ) : null}
                      <span className="block text-xs text-slate-600 truncate mt-1">
                        {contact.preview || "Iniciar conversa"}
                      </span>
                    </span>
                    <span className="flex flex-col items-end gap-1">
                      <span className="text-[10px] text-slate-400">
                        {formatRelativeTime(contact.last_message_at)}
                      </span>
                      {contact.unread_count > 0 && !isOpen ? (
                        <ContactBadge>
                          {contact.unread_count > 99
                            ? "99+"
                            : contact.unread_count}
                        </ContactBadge>
                      ) : null}
                    </span>
                  </ContactButton>
                );
              })}
            </PanelList>
          </DockPanel>
        )}

        <DockBar>
          <DockLabel
            type="button"
            className="sc-dock__label"
            aria-expanded={panelOpen}
            onClick={() => setPanelOpen((open) => !open)}
          >
            <MessageSquare size={18} />
            <span>Mensagens</span>
            {totalUnread > 0 ? (
              <TotalBadge>{totalUnread > 99 ? "99+" : totalUnread}</TotalBadge>
            ) : null}
            <Chevron className="sc-dock__chevron">
              <ChevronDown size={14} />
            </Chevron>
          </DockLabel>
        </DockBar>
      </DockBottom>
    </Dock>
  );
}
